'use client'

/**
 * Purpose: Multi-page skin survey shell — questionnaire or photo capture path.
 * Inputs: locale and optional prefillProfile from authenticated onboarding.
 * Outputs: Renders page components, progress bar, and navigation via useSurveyForm.
 */

import { useTranslations } from 'next-intl'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'
import { SurveyCameraCapture } from '@components/captures/SurveyCameraCapture/SurveyCameraCapture'
import {
  useSurveyForm,
  TOTAL_PAGES,
  type SurveyPrefillProfile,
} from '@components/forms/SurveyForm/hooks/use-survey-form'
import { SurveyWelcomePage } from '@components/forms/SurveyForm/pages/SurveyWelcomePage'
import { SurveyClientInfoPage } from '@components/forms/SurveyForm/pages/SurveyClientInfoPage'
import { SurveyKnowledgePage } from '@components/forms/SurveyForm/pages/SurveyKnowledgePage'
import { SurveyOilyDryPage } from '@components/forms/SurveyForm/pages/SurveyOilyDryPage'
import { SurveySensitivePage } from '@components/forms/SurveyForm/pages/SurveySensitivePage'
import { SurveyPigmentedPage } from '@components/forms/SurveyForm/pages/SurveyPigmentedPage'
import { SurveyWrinkledPage } from '@components/forms/SurveyForm/pages/SurveyWrinkledPage'
import { SurveyConsentPage } from '@components/forms/SurveyForm/pages/SurveyConsentPage'

// Multi-page shell — questionnaire path or optional photo capture via useSurveyForm.
export type { SurveyPrefillProfile }

export function SurveyForm({
  locale,
  prefillProfile,
}: {
  locale: string
  prefillProfile?: SurveyPrefillProfile
}) {
  const tPages = useTranslations('survey.pages')
  const tPhoto = useTranslations('survey.photo')
  // Page index, photo vs questionnaire mode, and navigation handlers from the form hook.
  const {
    t,
    page,
    formState,
    setFormState,
    pageError,
    actionError,
    isPending,
    mode,
    analyzed,
    photoEnabled,
    inPhotoCapture,
    stepLabel,
    progress,
    // Navigation and submit handlers wired to server actions in the hook.
    handleAnalyzed,
    switchToQuestionnaire,
    updateSection,
    setKnowledge,
    setSlider,
    handleNext,
    handleBack,
    handleSubmit,
    setMode,
  } = useSurveyForm({ locale, prefillProfile })

  // Shell: progress bar, page router, or full-screen camera capture.
  return (
    <div id="survey-form" className="max-w-2xl mx-auto px-4 py-12">
      {/* Progress hidden during full-screen camera capture. */}
      {!inPhotoCapture && (
        <div id="survey-progress" className="mb-8">
          <div className="flex justify-between text-xs text-black mb-1">
            <span>{stepLabel}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-black/10 rounded-full h-1.5">
            <div
              className="bg-orange h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Full-screen photo capture replaces the paginated questionnaire. */}
      {inPhotoCapture && (
        <SurveyCameraCapture
          locale={locale}
          onAnalyzed={handleAnalyzed}
          onUseQuestionnaire={switchToQuestionnaire}
        />
      )}

      {!inPhotoCapture && (
        <div id="survey-page-content" className={page === 0 ? '' : 'min-h-[400px]'}>
          {/* Photo path: prompt user to refine low-confidence axes after analysis. */}
          {mode === 'photo' && analyzed && page >= 3 && page <= 6 && (
            <p id="survey-photo-refine-intro" className="text-black/70 text-sm mb-4 p-3 bg-lavender/20 rounded-lg">
              {tPhoto('refineIntro')}
            </p>
          )}

          {page === 0 && (
            <SurveyWelcomePage
              title={t('title')}
              description={tPages('welcome.description')}
              startSurveyText={t('startSurveyText')}
              takePhotoInsteadText={t('takePhotoInstead')}
              photoEnabled={photoEnabled}
              onStart={handleNext}
              onTakePhoto={() => setMode('photo')}
            />
          )}

          {/* Page 1 — contact info (skipped when prefillProfile is set). */}
          {page === 1 && (
            <SurveyClientInfoPage
              formState={formState}
              setFormState={setFormState}
              prefillProfile={prefillProfile}
              title={tPages('clientInfo.title')}
              tPages={tPages}
            />
          )}

          {/* Page 2 — self-knowledge gates for slider vs questionnaire. */}
          {page === 2 && (
            <SurveyKnowledgePage
              formState={formState}
              setKnowledge={setKnowledge}
              title={tPages('skinKnowledge.title')}
              description={tPages('skinKnowledge.description')}
              yesLabel={tPages('skinKnowledge.yes')}
              noLabel={tPages('skinKnowledge.no')}
              tPages={tPages}
            />
          )}

          {/* Pages 3–6 — one skin-type axis questionnaire each. */}
          {page === 3 && (
            <SurveyOilyDryPage
              formState={formState}
              updateSection={updateSection}
              setSlider={setSlider}
              tPages={tPages}
            />
          )}

          {/* Sensitive / pigmented / wrinkled axis pages follow the same slider-or-questions pattern. */}
          {page === 4 && (
            <SurveySensitivePage
              formState={formState}
              updateSection={updateSection}
              setSlider={setSlider}
              tPages={tPages}
            />
          )}

          {/* Pigmented axis — melanin and dark-spot tendency. */}
          {page === 5 && (
            <SurveyPigmentedPage
              formState={formState}
              updateSection={updateSection}
              setSlider={setSlider}
              tPages={tPages}
            />
          )}

          {/* Wrinkled axis — elasticity and visible line depth. */}
          {page === 6 && (
            <SurveyWrinkledPage
              formState={formState}
              updateSection={updateSection}
              setSlider={setSlider}
              tPages={tPages}
            />
          )}

          {/* Final step — consent checkbox or photo-path note before submit. */}
          {page === 7 && (
            <SurveyConsentPage
              formState={formState}
              setFormState={setFormState}
              actionError={actionError}
              locale={locale}
              mode={mode}
              title={tPages('consent.title')}
              description={tPages('consent.description')}
              consentNote={tPhoto('consentNote')}
              tPages={tPages}
            />
          )}
        </div>
      )}

      {/* Client-side validation surfaced before page advance — assertive so it interrupts. */}
      {pageError && (
        <p id="survey-page-error" role="alert" aria-live="assertive" className="text-red-600 text-sm mt-2 mb-4">
          {pageError}
        </p>
      )}

      {/* Back / next / submit footer — hidden on the welcome page. */}
      {page > 0 && (
        <div id="survey-navigation" className="flex mt-8 justify-between">
          <HoverGradientButton
            variant="outline"
            onClick={handleBack}
            disabled={isPending}
            className="cursor-pointer"
          >
            {t('back')}
          </HoverGradientButton>

          {/* Next advances validation; last page submits via server action. */}
          {page < TOTAL_PAGES - 1 ? (
            <HoverGradientButton onClick={handleNext} className="cursor-pointer">
              {t('pageNextText')}
            </HoverGradientButton>
          ) : (
            <HoverGradientButton
              onClick={handleSubmit}
              // Consent must be checked on the final page before submit runs.
              disabled={isPending || !formState.consentGiven}
              className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Submitting…' : t('completeText')}
            </HoverGradientButton>
          )}
        </div>
      )}
    </div>
  )
}
